import { Apps } from '@mui/icons-material';
import { Autocomplete, Box, TextField, useTheme } from '@mui/material';
import {
    AbdomenChestOutlineIcon,
    BodyOutlineIcon,
    BrainOutlineIcon,
    CombinationOutlineIcon,
    FemalePelvisOutlineIcon,
    HeadNeckOutlineIcon,
    HeadOutlineIcon,
    TorsoOutlineIcon,
} from '~/assets/prediction-model-icons';

export const DEFAULT_BODY_PARTS = [
    { icon: Apps, label: 'All' },
    { icon: HeadOutlineIcon, label: 'Head' },
    { icon: BrainOutlineIcon, label: 'Brain' },
    { icon: HeadNeckOutlineIcon, label: 'Head and Neck' },
    { icon: AbdomenChestOutlineIcon, label: 'Abdomen and Chest' },
    { icon: TorsoOutlineIcon, label: 'Torso' },
    { icon: FemalePelvisOutlineIcon, label: 'Pelvis' },
    { icon: CombinationOutlineIcon, label: 'Combination' },
    { icon: BodyOutlineIcon, label: 'Whole Body' },
];

type UploadedBodyPartProps = {
    selectedBodyPart: number;
    setSelectedBodyPart: (part: number) => void;
    bodyParts?: typeof DEFAULT_BODY_PARTS;
};
export default function UploadedBodyPart({
    selectedBodyPart,
    setSelectedBodyPart,
    bodyParts = DEFAULT_BODY_PARTS,
}: UploadedBodyPartProps) {
    const theme = useTheme();
    const defaultValue = bodyParts[0];

    const handleAutocompleteChange = (
        _: unknown,
        newValue: (typeof bodyParts)[number]
    ) => {
        const index = bodyParts.findIndex(
            (part) => part.label === newValue?.label
        );
        if (index !== -1) {
            setSelectedBodyPart(index);
        }
    };

    return (
        <Autocomplete
            options={bodyParts}
            fullWidth
            autoHighlight
            defaultValue={defaultValue}
            getOptionLabel={(option) => option.label}
            value={bodyParts[selectedBodyPart]}
            onChange={handleAutocompleteChange}
            disableClearable
            renderOption={(props, option) => {
                return (
                    <Box component="li" {...props} display="flex" gap={2}>
                        <option.icon
                            fill={theme.palette.text.primary}
                            stroke={theme.palette.text.primary}
                            width="20px"
                            height="20px"
                        />
                        {option.label}
                    </Box>
                );
            }}
            renderInput={(params) => {
                const selectedOption = params.inputProps.value
                    ? bodyParts.find(
                          (part) => part.label === params.inputProps.value
                      )
                    : defaultValue;

                return (
                    <TextField
                        {...params}
                        fullWidth
                        label="Body part"
                        size="small"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedOption ? (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    ml={1}
                                >
                                    <selectedOption.icon
                                        fill={theme.palette.text.primary}
                                        stroke={theme.palette.text.primary}
                                        width="14px"
                                        height="14px"
                                    />
                                </Box>
                            ) : null,
                        }}
                    />
                );
            }}
        />
    );
}
